import { CloseIcon } from "@chakra-ui/icons";
import {
  Button,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  Text,
  FormLabel,
  Box,
} from "@chakra-ui/react";
import { useState, useMemo } from "react";
import Spinner from "common/components/spinner/Spinner";
import { globalStyles } from "common/theme/styles";
import ReactSelect from "react-select";
import "services/Recruit/assets/css/CreateJobOpening/style.css";

const REVOCATION_REASONS = [
  {
    label: "Candidate-Related Reasons",
    options: [
      {
        value: "Candidate did not respond",
        label: "Candidate did not respond",
      },
      {
        value: "Candidate revoked interest",
        label: "Candidate revoked interest",
      },
      {
        value: "Candidate failed background verification",
        label: "Candidate failed background verification",
      },
      {
        value: "Candidate failed document verification",
        label: "Candidate failed document verification",
      },
      {
        value: "Candidate did not join on the expected date",
        label: "Candidate did not join on the expected date",
      },
      {
        value: "Candidate provided incorrect or incomplete information",
        label: "Candidate provided incorrect or incomplete information",
      },
      {
        value: "Candidate salary expectations changed",
        label: "Candidate salary expectations changed",
      },
      {
        value: "Candidate accepted another offer",
        label: "Candidate accepted another offer",
      },
    ],
  },
  {
    label: "Company / Internal Reasons",
    options: [
      { value: "Position closed", label: "Position closed" },
      { value: "Budget constraints", label: "Budget constraints" },
      {
        value: "Internal restructuring / Reorganization",
        label: "Internal restructuring / Reorganization",
      },
      { value: "Hiring freeze", label: "Hiring freeze" },
      {
        value: "Role requirements changed",
        label: "Role requirements changed",
      },
      {
        value: "Project canceled or delayed",
        label: "Project canceled or delayed",
      },
      {
        value: "Duplicate or erroneous offer issued",
        label: "Duplicate or erroneous offer issued",
      },
    ],
  },
  {
    label: "Compliance / Policy Reasons",
    options: [
      {
        value: "Non-compliance with company policy",
        label: "Non-compliance with company policy",
      },
      {
        value: "Legal or regulatory compliance concerns",
        label: "Legal or regulatory compliance concerns",
      },
    ],
  },
];

const OTHER_OPTION = {
  value: "Other (please specify)",
  label: "Other (please specify)",
};

const RevokeOfferModal = ({ isOpen, onClose, onConfirm, isLoading }) => {
  const [selectedReason, setSelectedReason] = useState(null);
  const [otherReason, setOtherReason] = useState("");
  const [error, setError] = useState("");

  // Flatten all options for ReactSelect
  const revocationReasonOptions = useMemo(() => {
    const allOptions = [];
    REVOCATION_REASONS.forEach((category) => {
      category.options.forEach((option) => {
        allOptions.push(option);
      });
    });
    allOptions.push(OTHER_OPTION);
    return allOptions;
  }, []);

  const handleClose = () => {
    setSelectedReason(null);
    setOtherReason("");
    setError("");
    onClose();
  };

  const handleSubmit = () => {
    if (!selectedReason) {
      setError("Please select a reason for revocation");
      return;
    }

    if (selectedReason.value === OTHER_OPTION.value) {
      if (!otherReason || otherReason.trim() === "") {
        setError("Please provide details for the revocation reason");
        return;
      }
      if (otherReason.trim().length < 10) {
        setError("Please provide at least 10 characters for the reason");
        return;
      }
      setError("");
      onConfirm(otherReason.trim());
    } else {
      setError("");
      onConfirm(selectedReason.value);
    }
  };

  return (
    <Modal onClose={handleClose} isOpen={isOpen} isCentered>
      <ModalOverlay />
      <ModalContent maxWidth="500px" height="450px">
        <ModalHeader>Revoke Offer Letter</ModalHeader>
        <IconButton
          size="xs"
          className="closeIcon"
          onClick={handleClose}
          icon={<CloseIcon />}
        />

        <ModalBody>
          <FormLabel
            display="flex"
            ms="4px"
            fontSize="sm"
            fontWeight="500"
            mb="8px"
          >
            Reason for Revocation
            <Text color="red" ms="2px">
              *
            </Text>
          </FormLabel>
          <Box
            mb={
              selectedReason && selectedReason.value === OTHER_OPTION.value
                ? 4
                : 0
            }
            w="100%"
          >
            <ReactSelect
              value={selectedReason}
              onChange={(selectedOption) => {
                setSelectedReason(selectedOption);
                if (error) setError("");
              }}
              options={revocationReasonOptions}
              placeholder="Select a reason for revocation"
              className="basic-single-select reactSelect"
              classNamePrefix="select"
              isClearable
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                control: (provided) => ({
                  ...provided,
                  borderColor:
                    error && !selectedReason
                      ? globalStyles.colors.errorText
                      : globalStyles.colors.borderColor,
                  minHeight: "40px",
                  width: "100%",
                }),
                container: (provided) => ({
                  ...provided,
                  width: "100%",
                }),
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
            />
          </Box>

          {selectedReason && selectedReason.value === OTHER_OPTION.value && (
            <>
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                mb="8px"
                mt={4}
              >
                Please specify the reason
                <Text color="red" ms="2px">
                  *
                </Text>
              </FormLabel>
              <Textarea
                value={otherReason}
                onChange={(e) => {
                  setOtherReason(e.target.value);
                  if (error) setError("");
                }}
                placeholder="Please provide a detailed reason for revoking this offer letter..."
                size="md"
                minH="120px"
                resize="vertical"
                borderColor={
                  error &&
                  selectedReason &&
                  selectedReason.value === OTHER_OPTION.value
                    ? globalStyles.colors.errorText
                    : globalStyles.colors.borderColor
                }
                fontSize="sm"
              />
            </>
          )}

          {error && (
            <Text color={globalStyles.colors.errorText} fontSize="sm" mt={2}>
              {error}
            </Text>
          )}
          <Text color="gray.500" fontSize="xs" mt={2}>
            This reason will be included in the revocation email sent to the
            candidate.
          </Text>
        </ModalBody>

        <ModalFooter>
          <Button
            className="theme-btn2"
            variant="outline"
            size="sm"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            className="theme-btn3"
            colorScheme="orange"
            size="sm"
            ml={4}
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? <Spinner /> : "Revoke Offer"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RevokeOfferModal;
